import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SourceData } from '@models/SourceData';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { AppConstants } from '@constants/app.constant';

/**
 * Popup component for displaying source data with sorting and pagination.
 * Used to show details such as expiry dates for various IDs.
 */
@Component({
  selector: 'app-source-popup',
  templateUrl: './source-popup.component.html',
  styleUrl: './source-popup.component.scss',
  standalone: true,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
})
export class SourcePopupComponent implements OnInit, AfterViewInit {
  /** Active modal instance for closing the popup */
  activeModal = inject(NgbActiveModal);
  /** Application constants */
  constants = AppConstants;

  /** Source data to display in the table */
  @Input() sourceData: SourceData[] = [];
  /** Type of ID being displayed (e.g., DEA, SLN, etc.) */
  @Input() type!: string;
  /** Value of the ID being displayed */
  @Input() typeValue!: string;
  /** Sort instance for the table */
  @ViewChild('sortSource', { static: false }) sortSource!: MatSort;
  /** Data source for the table */
  sourceDataMp!: MatTableDataSource<SourceData>;
  /** Columns displayed in the table */
  displayedColumns: string[] = ['name', 'id_type', 'id_value', 'expiry_date','source'];

  /** Total number of items in the table */
  totalItems = 0;
  /** Page size for the table */
  pageSize = 5;
  /** Current page index */
  page = 0;
  /** Paginator instance for the table */
  @ViewChild('sourcePaginator') rPaginator!: MatPaginator;
  @ViewChild('sourcePaginator') set rMatPaginator(mpr: MatPaginator) {
    this.rPaginator = mpr;
    this.setupPaginatorCR();
  }

  ngOnInit(): void {
    this.sourceDataMp = new MatTableDataSource<SourceData>(this.sourceData);
    this.totalItems = this.sourceData.length;
    this.sourceDataMp.paginator = this.rPaginator;
  }

  ngAfterViewInit(): void {
    this.setupSortSource();
  }

  /**
   * Sets up the paginator for the table.
   */
  setupPaginatorCR(): void {
    if (this.rPaginator !== undefined) {
      this.sourceDataMp.paginator = this.rPaginator;
      this.rPaginator.pageIndex = this.page;
      this.rPaginator.pageSize = this.pageSize;
      this.rPaginator.length = this.totalItems;
    }
  }

  /**
   * Handles page change events for the table.
   */
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.page = event.pageIndex;
  }

  /**
   * Sets up the default sorting for the table.
   */
  setupSortSource(): void {
    this.sortSource.active = 'name';
    this.sortSource.direction = 'asc';
    this.sourceDataMp.sort = this.sortSource;
    this.sortSource.sortChange.emit({
      active: this.sortSource.active,
      direction: this.sortSource.direction,
    });
  }

  /**
   * Checks if the current date is greater than the input date string.
   * Used to determine if an ID is expired.
   */
  isCurrentDateGreater(inputDateStr: string): boolean {
    if (!inputDateStr) {
      return true;
    }
    const inputDate = new Date(inputDateStr);
    const currentDate = new Date();
    inputDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate > inputDate;
  }
}
